import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';
import { NR_ATTR_ANALYTICS_ID } from '@shared/utils/logger';
import { isChunkLoadErrorWhileUnloading } from '@shared/utils/pageUnloading';
import { isReportedError } from '@shared/utils/reportedErrors';
import { withWindow } from '@shared/utils/withWindow';
import { config } from '@static/js/env.config';

export const initNewRelic = () => {
  withWindow(window => {
    const { newrelic, buildNumber } = window;

    if (newrelic) {
      newrelic.setCustomAttribute('buildNumber', buildNumber);
      newrelic.setCustomAttribute('env', config.joyEnvironment);
      newrelic.setCustomAttribute(NR_ATTR_ANALYTICS_ID, getAnalyticsId());
      newrelic.addRelease('joy-web', buildNumber);
    }
  });
};

const URL_IN_TEXT = /[a-z][a-z\d+.-]*:\/\//i;

/**
 * The browser hides errors from cross-origin scripts loaded without CORS behind a bare "Script error."
 * with no filename or stack, so NR has nothing to group or act on.
 */
const isOpaqueScriptError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message, sourceURL, line, stack } = error as Partial<Record<'message' | 'sourceURL' | 'line' | 'stack', unknown>>;
  return message === 'Script error.' && !sourceURL && !line && !(typeof stack === 'string' && URL_IN_TEXT.test(stack));
};

// Segment's GA4 destination loads gtag through this first-party proxy path; its load failures reject inside Segment's code.
const ANALYTICS_PROXY_PATH = '/_p/metrics/';

const ANALYTICS_PROXY_LOAD_FAILURE = new RegExp(`^(Unhandled Promise Rejection: )?Failed to load https?://[^/]+${ANALYTICS_PROXY_PATH}`);

const isAnalyticsProxyLoadFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && ANALYTICS_PROXY_LOAD_FAILURE.test(message);
};

/** Safari's "runtime.sendMessage" and "No Listener: tabs:" rejections come only from extension messaging APIs. */
const EXTENSION_ONLY_MESSAGE =
  /^(Unhandled Promise Rejection: )?(Failed to connect to MetaMask|Extension context invalidated\.|Invalid call to runtime\.sendMessage\(\)\. Tab not found\.|No Listener: tabs:[\w.-]+)$/;

const EXTENSION_SCHEMES = new Set(['chrome-extension', 'moz-extension', 'safari-extension', 'safari-web-extension']);

const CHROME_FRAME = /^\s*at\s/;

/** Firefox and Safari frames are "fn@location:line:col"; Safari names can hold spaces ("global code@..."). */
const AT_SIGN_FRAME = /^[^@]*@\S+:\d+(:\d+)?$/;

/** A location ending in ":line" or ":line:col", optionally inside Chrome's parentheses. */
const LOCATED_FRAME = /:\d+(:\d+)?\)?$/;

/** Extensions wrapping XHR or fetch from an injected inline script leave "<inline>:line:col" frames. */
const INLINE_FRAME = /[\s(@]<inline>:\d+(:\d+)?\)?$/;

const isNeutralFrame = (line: string): boolean => line.includes('<anonymous>') || INLINE_FRAME.test(line);

const MAX_FRAME_LINE_LENGTH = 2000;

const SCHEME_CHAR = /[a-z\d+.-]/i;

/** Linear scan: every "://" is read back to the start of its scheme, and scheme chars never include ":" or "/". */
const schemesIn = (line: string): string[] => {
  const schemes: string[] = [];
  for (let separator = line.indexOf('://'); separator !== -1; separator = line.indexOf('://', separator + 3)) {
    let start = separator;
    while (start > 0 && SCHEME_CHAR.test(line[start - 1])) start--;
    schemes.push(line.slice(start, separator).toLowerCase());
  }
  return schemes;
};

/**
 * Wallet and other extensions inject scripts into our pages, and NR attributes their failures to us.
 * Only drop the error when it has an extension frame and every other located frame is `<anonymous>` or `<inline>`; any other frame keeps it.
 * Errors we reported ourselves are always kept: an extension wrapping `fetch` can leave only its own frames on them.
 */
const isBrowserExtensionError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || isReportedError(error)) return false;
  const { message, stack } = error as Partial<Record<'message' | 'stack', unknown>>;
  if (typeof message === 'string' && EXTENSION_ONLY_MESSAGE.test(message)) return true;
  if (typeof stack !== 'string') return false;

  let hasExtensionFrame = false;
  for (const line of stack.split('\n')) {
    if (line.length > MAX_FRAME_LINE_LENGTH) continue;
    if (!CHROME_FRAME.test(line) && !AT_SIGN_FRAME.test(line)) continue;
    const schemes = schemesIn(line);
    if (schemes.length === 0) {
      if (LOCATED_FRAME.test(line) && !isNeutralFrame(line)) return false;
      continue;
    }
    if (!schemes.every(scheme => EXTENSION_SCHEMES.has(scheme))) return false;
    hasExtensionFrame = true;
  }
  return hasExtensionFrame;
};

/** Spec-defined warnings (the browser defers the notifications one frame) that Chrome and Safari surface as window error events with no stack. */
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

const isResizeObserverLoopError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && RESIZE_OBSERVER_LOOP.test(message);
};

const isUnreportableError = (error: unknown): boolean =>
  isChunkLoadErrorWhileUnloading(error) || isOpaqueScriptError(error) || isAnalyticsProxyLoadFailure(error) || isBrowserExtensionError(error) || isResizeObserverLoopError(error);

/**
 * Covers errors NR captures on its own (unhandled rejections, window errors), not just the ones we notice.
 * NR keeps only one error handler, so every filter goes through `isUnreportableError`.
 */
export const ignoreUnreportableErrors = () => {
  withWindow(window => {
    window.newrelic?.setErrorHandler(isUnreportableError);
  });
};
